import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	App,
	Button,
	Card,
	Checkbox,
	DatePicker,
	Input,
	InputNumber,
	Modal,
	Radio,
	Select,
	Space,
	Typography,
} from 'antd';
import dayjs from 'dayjs';

import ConditionBuilder from '../common/ConditionBuilder';
import ScheduleFields, {
	browserTimezone as sharedBrowserTimezone,
	timesOf,
	timesToSchedule,
} from './ScheduleFields';
import MeasurementValueInput from '../common/MeasurementValueInput';
import CurrencyValueInput from '../common/CurrencyValueInput';
import NotifyActionFields, {
	KNOWN_NOTIFY_KEYS,
	buildNotifyAction,
	emptyNotifyDraft,
	notifyDraftFromAction,
	useCollectionAudienceOptions,
} from './NotifyActionFields';
import NotifyPreviewButton from './NotifyPreviewButton';
import TokenInput from '../common/TokenInput';
import { buildChoiceOptions, rendersAsChoice } from '../common/ChoiceOptions';
import { ITEMS_LIMIT } from '../common/ItemLookupSelect';
import { InputSelect, optionSearchFilter } from '../Input';
import { api } from '../../api/client';
import { API_ITEMS_URL } from '../../utils';
import { buildCardHierarchyOptions } from '../cards/CardVisuals';
import { buildScheduleTokens } from '../../utils/automationTokens';
import { createAutomation, patchAutomation } from '../../services/automations';
import { getItemDisplayValue } from '../../utils/items';
import { booleanLabels, getNumericInputMode } from '../../utils/elements';
import { normalizeId } from '../../utils/ids';
import { toStoredDate, toStoredDateTime } from '../../utils/dateDisplay';
import { isMeasurementValue } from '../../utils/units';
import { isCurrencyValue } from '../../utils/currency';
import {
	WINDOW_MINUTES_MAX,
	WINDOW_UNIT_MINUTES,
	WINDOW_UNIT_OPTIONS,
	normalizeSchedule,
	splitWindowMinutes,
	unitIsTimed,
	windowMinutesFrom,
	windowQtyMax,
} from '../../utils/automationSchedule';
import { CLOSE, PLUS } from '../icons';
import { isFileElement } from '../../utils/fileValues';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from '../items/QueryRefusalPick';

/** The elements a call to action may prefill: never driven, never a file (PR 10b), never a "Me too" count (KPR-390). */
export const prefillableElements = elements => ( elements || [] )
	.filter( el => el?.name && ! el?.options?.drivenFrom && ! isFileElement( el ) && el?.dataType !== 'confirmations' );

/**
 * Create/edit one expected-item reminder (`kind: 'expected-item'`).
 *
 * "Items of this shape should exist on this cadence; when they don't, notify."
 * Three questions in order — what should exist, how often we look, and who
 * hears about it — each section appearing once the one before it is answerable,
 * the same progressive shape as the event-rule editor and DrivenFromModal.
 *
 * Three things about this kind are worth knowing while reading:
 *
 * - The cadence is QUANTITY + UNIT (`every 2 weeks`, `every 6 hours`), which is
 *   what the API stores. `anchor` — the field that decides WHICH fortnight — is
 *   the server's to stamp and is never emitted from here; see
 *   utils/automationSchedule.js.
 * - The schedule carries an IANA timezone and the API REQUIRES it: all window
 *   math is zone-local, and nothing else in the platform stores a timezone. The
 *   browser's own zone is the default, which is right far more often than not.
 * - There is no trigger item on a clock, so `{{trigger.*}}` tokens are rejected
 *   at authoring — the token vocabulary here is `{{today}}`/`{{now}}` plus
 *   `{{last.*}}` (the newest matching record), and the KQL condition may not
 *   carry `{{ }}` at all.
 *
 * As in AutomationRuleEditor, the API validates the whole shape at write time
 * with field-level messages; this editor builds the documented JSON, omits
 * empty optionals, carries unknown stored keys through untouched, and surfaces
 * a rejection verbatim rather than growing a second copy of the rules.
 */

const { Text } = Typography;

// The keys this editor owns on a stored `check`; anything else rides along.
const KNOWN_CHECK_KEYS = [ 'card_id', 'condition', 'window', 'minCount', 'renotifyAfterMinutes' ];

const NUMERIC_DATA_TYPES = new Set( [ 'number', 'decimal', 'integer', 'rating' ] );

/**
 * Re-exported: the implementation lives with the cadence controls in
 * ScheduleFields now, which is where every schedule-triggered kind gets it.
 */
export const browserTimezone = sharedBrowserTimezone;

let keySeq = 0;
const nextKey = () => `p${ ++keySeq }`;
const emptyRow = () => ( { key: nextKey(), name: '', value: '' } );

const omitKeys = ( obj, keys ) => Object.fromEntries(
	Object.entries( obj || {} ).filter( ( [ k ] ) => ! keys.includes( k ) )
);

const elementLabelOf = el => el?.label?.singular || el?.name;

// Each row remembers the ORIGINAL value alongside its editable one: a stored
// prefill of `qty: 5` must round-trip as the number 5 unless the manager
// actually retyped it. The typed controls below produce typed values already
// (an InputNumber gives a number, a DatePicker an ISO string), but the guard
// stays because it is what makes an UNTOUCHED row byte-identical rather than
// merely equivalent — including for a stored value no control here can
// reproduce (a boolean, an array, a value from a future release).
// A measurement or currency prefill is an object its control edits as
// itself; every other object has no control here and is shown as text.
const isTypedObject = value => isMeasurementValue( value ) || isCurrencyValue( value );
const rowsFromObject = obj => Object.entries( obj || {} ).map( ( [ name, value ] ) => ( {
	key: nextKey(),
	name,
	value: value === null || value === undefined
		? ''
		: ( typeof value === 'object' && ! isTypedObject( value ) ? `${ value }` : value ),
	original: value,
} ) );

// Sameness for the untouched-row guard: primitives by their text, a
// measurement object by its parts (two objects always differ by identity,
// and `${ {} }` would call every edit of one "unchanged").
const sameRowValue = ( a, b ) => {
	if ( isMeasurementValue( a ) || isMeasurementValue( b ) ) {
		return isMeasurementValue( a ) && isMeasurementValue( b ) && a.value === b.value && a.unit === b.unit;
	}
	if ( isCurrencyValue( a ) || isCurrencyValue( b ) ) {
		return isCurrencyValue( a ) && isCurrencyValue( b ) && a.amount === b.amount && a.currency === b.currency;
	}
	return `${ a ?? '' }` === `${ b ?? '' }`;
};

const rowValue = row => {
	const current = row?.value ?? '';
	if ( row && 'original' in row && sameRowValue( row.original, current ) ) return row.original;
	return current;
};

export default function ExpectedItemRuleEditor( {
	open,
	collectionId,
	rule,
	collectionCards = [],
	cardsById = {},
	getElementsForCard,
	onClose,
	onSaved,
} ) {

	const { message } = App.useApp();

	// '12h' | '24h'. Read from the shared display store rather than the
	// preferences context so the editor still renders (at the default clock)
	// wherever there is no provider.

	const [ name, setName ] = useState( '' );
	const [ cardId, setCardId ] = useState( '' );

	const [ every, setEvery ] = useState( 1 );
	const [ unit, setUnit ] = useState( 'day' );
	const [ time, setTime ] = useState( '09:00' );
	// Several checks a day are ONE rule (the API's `times`); null means the
	// rule has a single time, which is how every pre-`times` rule reads.
	const [ times, setTimes ] = useState( null );
	const [ daysOfWeek, setDaysOfWeek ] = useState( [] );
	const [ dayOfMonth, setDayOfMonth ] = useState( null );
	const [ timezone, setTimezone ] = useState( browserTimezone );

	const [ kql, setKql ] = useState( '' );
	const [ windowType, setWindowType ] = useState( 'sinceMidnight' );
	// The window is EDITED as quantity + unit and STORED as minutes; the
	// canonical number is derived, never a third piece of state that could
	// disagree with the two the user is actually looking at.
	const [ windowQty, setWindowQty ] = useState( 60 );
	const [ windowUnit, setWindowUnit ] = useState( 'minute' );
	const [ minCount, setMinCount ] = useState( 1 );
	const [ renotifyAfterMinutes, setRenotifyAfterMinutes ] = useState( null );

	const [ notifyDraft, setNotifyDraft ] = useState( emptyNotifyDraft );
	const [ notifyExtras, setNotifyExtras ] = useState( {} );
	const [ checkExtras, setCheckExtras ] = useState( {} );

	const [ ctaEnabled, setCtaEnabled ] = useState( false );
	const [ ctaLabel, setCtaLabel ] = useState( '' );
	const [ ctaCardId, setCtaCardId ] = useState( '' );
	const [ ctaRows, setCtaRows ] = useState( [] );
	const [ ctaExtras, setCtaExtras ] = useState( {} );
	// The FULL stored ctas array. This editor renders only the first
	// create-item CTA; the rest (additional buttons, future types) must ride
	// through a no-op save untouched — the PR D lesson, again.
	const [ originalCtas, setOriginalCtas ] = useState( [] );

	// Items of the cards a card-lookup prefill points at, fetched lazily —
	// only when a row actually becomes a lookup. A reminder whose CTA prefills
	// text must not fire a collection-wide item query.
	const [ lookupItems, setLookupItems ] = useState( {} );
	const requestedLookups = useRef( new Set() );

	const [ saving, setSaving ] = useState( false );

	// Seed on OPEN, never straight off the prop — Cancel has to discard edits.
	useEffect( () => {
		if ( ! open ) return;
		// Legacy `freq` shapes normalize for DISPLAY exactly as they do
		// server-side, so an unedited old rule opens showing what it does.
		const schedule = normalizeSchedule( rule?.trigger?.schedule );
		const check = rule?.check || {};
		const action = ( Array.isArray( rule?.actions ) ? rule.actions : [] ).find( a => a?.type === 'notify' ) || {};
		const cta = ( Array.isArray( action.ctas ) ? action.ctas : [] ).find( c => c?.type === 'create-item' );

		setName( rule?.name || '' );
		setCardId( normalizeId( check.card_id ) || '' );

		setEvery( Number.isFinite( schedule.every ) && schedule.every > 0 ? schedule.every : 1 );
		setUnit( schedule.unit || 'day' );
		setTime( schedule.time || '09:00' );
		setDaysOfWeek( Array.isArray( schedule.daysOfWeek ) ? schedule.daysOfWeek : [] );
		setDayOfMonth( Number.isFinite( schedule.dayOfMonth ) ? schedule.dayOfMonth : null );
		setTimezone( schedule.timezone || browserTimezone() );
		setTimes( Array.isArray( schedule.times ) && schedule.times.length ? schedule.times : null );

		setKql( check.condition?.kql || '' );
		setWindowType( check.window?.type === 'rolling' ? 'rolling' : 'sinceMidnight' );
		{
			const split = splitWindowMinutes(
				Number.isFinite( check.window?.minutes ) ? check.window.minutes : 60
			);
			setWindowQty( split.qty );
			setWindowUnit( split.unit );
		}
		setMinCount( Number.isFinite( check.minCount ) ? check.minCount : 1 );
		setRenotifyAfterMinutes(
			Number.isFinite( check.renotifyAfterMinutes ) ? check.renotifyAfterMinutes : null
		);

		setNotifyDraft( rule ? notifyDraftFromAction( action ) : emptyNotifyDraft() );
		setNotifyExtras( omitKeys( action, KNOWN_NOTIFY_KEYS ) );
		setCheckExtras( omitKeys( check, KNOWN_CHECK_KEYS ) );

		setCtaEnabled( !! cta );
		setCtaLabel( cta?.label || '' );
		setCtaCardId( normalizeId( cta?.card_id ) || '' );
		setCtaRows( cta ? rowsFromObject( cta.prefill ) : [] );
		setCtaExtras( omitKeys( cta, [ 'type', 'label', 'card_id', 'prefill' ] ) );
		setOriginalCtas( Array.isArray( action.ctas ) ? action.ctas : [] );

		setLookupItems( {} );
		requestedLookups.current = new Set();
	}, [ open, rule ] );

	const cardOptions = useMemo( () => buildCardHierarchyOptions( collectionCards ), [ collectionCards ] );

	const { groups, users } = useCollectionAudienceOptions( collectionId, open );

	const windowMinutes = useMemo(
		() => windowMinutesFrom( windowQty, windowUnit ),
		[ windowQty, windowUnit ]
	);
	const maxWindowQty = useMemo( () => windowQtyMax( windowUnit ), [ windowUnit ] );

	// What the two controls literally SAY, before the clamp. The quantity input
	// refuses an over-long value for its own unit, but nothing stops a quantity
	// that was legal in minutes from becoming 2000 DAYS when the unit changes
	// under it — and `windowMinutes` would then quietly clamp to 90 days. The
	// hint and the save guard both read this, so the editor never shows one
	// number while intending to store another.
	const requestedWindowMinutes = useMemo(
		() => Math.round( ( Number( windowQty ) || 0 ) * ( WINDOW_UNIT_MINUTES[ windowUnit ] || 1 ) ),
		[ windowQty, windowUnit ]
	);
	const windowTooLong = requestedWindowMinutes > WINDOW_MINUTES_MAX;

	const elementsFor = useCallback(
		id => ( id && getElementsForCard ? ( getElementsForCard( id ) || [] ) : [] ),
		[ getElementsForCard ]
	);

	const expectedElements = useMemo( () => elementsFor( cardId ), [ elementsFor, cardId ] );

	// {{last.*}} resolves against the EXPECTED card — one dot-walk hop through
	// its lookups, which is exactly what the API resolves.
	const tokens = useMemo(
		() => buildScheduleTokens( expectedElements, getElementsForCard ),
		[ expectedElements, getElementsForCard ]
	);

	const ctaElements = useMemo( () => elementsFor( ctaCardId ), [ elementsFor, ctaCardId ] );

	const ctaElementOptions = useMemo( () => prefillableElements( ctaElements )
		.map( el => ( { value: el.name, label: elementLabelOf( el ) } ) ),
	[ ctaElements ] );

	const ctaElementByName = useMemo(
		() => new Map( ctaElements.filter( el => el?.name ).map( el => [ el.name, el ] ) ),
		[ ctaElements ]
	);

	// The lookup cards the CURRENT rows need items for. Recomputed from the
	// rows, so choosing a lookup element is what triggers the fetch.
	const neededLookupCardIds = useMemo( () => {
		const ids = new Set();
		ctaRows.forEach( row => {
			const element = ctaElementByName.get( row?.name );
			if ( element?.dataType !== 'card-lookup' ) return;
			const targetId = normalizeId( element?.options?.lookupCardId );
			if ( targetId ) ids.add( targetId );
		} );
		return [ ...ids ];
	}, [ ctaRows, ctaElementByName ] );

	useEffect( () => {
		if ( ! open || ! collectionId || ! neededLookupCardIds.length ) return undefined;
		let active = true;
		neededLookupCardIds.forEach( lookupCardId => {
			if ( requestedLookups.current.has( lookupCardId ) ) return;
			requestedLookups.current.add( lookupCardId );
			setLookupItems( prev => ( { ...prev, [ lookupCardId ]: { loading: true, items: [] } } ) );
			// Descendants included for the same reason the lookup input includes
			// them: an item of a child card IS an item of its parent. The
			// ordinary items endpoint means the manager's own ACLs filter this.
			api.get( API_ITEMS_URL, {
				// Explicitly the endpoint's MAX_LIMIT: its default of 50 silently
				// hides most of a real card, and this list is not paged.
				params: {
					collection_id: collectionId,
					card_id: lookupCardId,
					include_descendants: true,
					limit: ITEMS_LIMIT,
				},
			} ).then( res => {
				if ( ! active ) return;
				setLookupItems( prev => ( {
					...prev,
					[ lookupCardId ]: { loading: false, items: Array.isArray( res?.data ) ? res.data : [] },
				} ) );
			} ).catch( () => {
				if ( ! active ) return;
				// An empty list still lets the row be typed past by switching
				// the element; the API validates the final prefill anyway.
				setLookupItems( prev => ( { ...prev, [ lookupCardId ]: { loading: false, items: [] } } ) );
			} );
		} );
		return () => { active = false; };
	}, [ open, collectionId, neededLookupCardIds ] );

	const lookupOptionsFor = useCallback( lookupCardId => {
		const entry = lookupItems[ lookupCardId ];
		const card = cardsById?.[ lookupCardId ];
		const elementDefs = elementsFor( lookupCardId );
		return ( entry?.items || [] )
			.map( item => ( {
				value: normalizeId( item?._id ),
				label: getItemDisplayValue( { card, elementDefs, item } ),
			} ) )
			.filter( option => option.value );
	}, [ lookupItems, cardsById, elementsFor ] );

	const patchCtaRow = useCallback( ( rowKey, patch ) => {
		setCtaRows( prev => prev.map( row => ( row.key === rowKey ? { ...row, ...patch } : row ) ) );
	}, [] );

	const buildCtas = useCallback( () => {
		const label = ( ctaLabel || '' ).trim();
		const rebuilt = ( ctaEnabled && label && ctaCardId )
			? ( () => {
				const prefill = {};
				ctaRows.forEach( row => {
					const key = ( row?.name || '' ).trim();
					if ( ! key ) return;
					prefill[ key ] = rowValue( row );
				} );
				return {
					...ctaExtras,
					type: 'create-item',
					label,
					card_id: ctaCardId,
					...( Object.keys( prefill ).length ? { prefill } : {} ),
				};
			} )()
			: null;

		// Splice the rebuilt CTA over the one this editor rendered (the FIRST
		// create-item entry), keeping every other stored CTA in place. With no
		// stored array, the rebuilt one stands alone.
		if ( ! originalCtas.length ) return rebuilt ? [ rebuilt ] : undefined;
		const out = [];
		let replaced = false;
		originalCtas.forEach( entry => {
			if ( ! replaced && entry?.type === 'create-item' ) {
				replaced = true;
				if ( rebuilt ) out.push( rebuilt );
				return; // disabled -> the edited CTA is removed, others remain
			}
			out.push( entry );
		} );
		if ( ! replaced && rebuilt ) out.push( rebuilt );
		return out.length ? out : undefined;
	}, [ ctaEnabled, ctaLabel, ctaCardId, ctaRows, ctaExtras, originalCtas ] );

	/** The cadence as ScheduleFields edits it: one object in, one object out,
	 * with the individual pieces still living in their own state (the rest of
	 * this editor reads `unit` and `daysOfWeek` directly). */
	const scheduleValue = useMemo( () => ( {
		every, unit, timezone, daysOfWeek, dayOfMonth,
		...( times && times.length > 1 ? { times } : { time } ),
	} ), [ every, unit, timezone, daysOfWeek, dayOfMonth, time, times ] );

	const handleScheduleChange = useCallback( next => {
		setEvery( Number.isFinite( Number( next.every ) ) ? Number( next.every ) : 1 );
		setUnit( next.unit );
		setTimezone( next.timezone );
		setDaysOfWeek( Array.isArray( next.daysOfWeek ) ? next.daysOfWeek : [] );
		setDayOfMonth( next.dayOfMonth ?? null );
		const list = timesOf( next );
		setTimes( list.length > 1 ? list : null );
		setTime( list[ 0 ] || '' );
	}, [] );

	/** The canonical cadence. Never carries `anchor` — the server owns it. */
	const buildSchedule = useCallback( () => {
		const schedule = { every: Number( every ) || 1, unit, timezone };
		// One time is spelled `time`, several `times` — the same normalization
		// the API applies, so a single-slot rule round-trips byte-identical and
		// its cadence never reads as changed.
		if ( unitIsTimed( unit ) ) Object.assign( schedule, timesToSchedule( times && times.length ? times : [ time ] ) );
		if ( unit === 'week' && daysOfWeek.length ) {
			schedule.daysOfWeek = [ ...daysOfWeek ].sort( ( a, b ) => a - b );
		}
		if ( unit === 'month' && dayOfMonth ) schedule.dayOfMonth = Number( dayOfMonth );
		// Omitted -> the anchor's day (validateSchedule leaves it out too); forcing
		// a default of 1 here read as a cadence change and re-anchored the rule.
		// A yearly reminder may simply mean "this month" — the day is optional.
		if ( unit === 'year' && Number.isFinite( Number( dayOfMonth ) ) && dayOfMonth !== null && dayOfMonth !== '' ) {
			schedule.dayOfMonth = Number( dayOfMonth );
		}
		return schedule;
	}, [ every, unit, timezone, time, times, daysOfWeek, dayOfMonth ] );

	const buildCheck = useCallback( () => {
		const check = { ...checkExtras, card_id: cardId };
		// No {{ }} here: a schedule has no trigger item, and the API says so.
		// An emptied box is an explicit NULL rather than an absent key: the
		// editor holds the whole check, and silence is on its way to meaning
		// "keep what is stored" — which would make clearing the condition here
		// look like it worked and change nothing.
		check.condition = kql.trim() ? { kql: kql.trim() } : null;
		check.window = windowType === 'rolling'
			? { type: 'rolling', minutes: Number( windowMinutes ) }
			// 'sinceSlot' measures from the PREVIOUS occurrence, which is the
			// window a several-times-a-day cadence needs: with slots, "since
			// midnight" lets the morning record answer for the evening check.
			: { type: windowType === 'sinceSlot' ? 'sinceSlot' : 'sinceMidnight' };
		check.minCount = Number( minCount ) || 1;
		if ( renotifyAfterMinutes !== null && renotifyAfterMinutes !== undefined && renotifyAfterMinutes !== '' ) {
			check.renotifyAfterMinutes = Number( renotifyAfterMinutes );
		}
		return check;
	}, [ checkExtras, cardId, kql, windowType, windowMinutes, minCount, renotifyAfterMinutes ] );

	const buildAction = useCallback(
		() => buildNotifyAction( notifyDraft, { extras: notifyExtras, ctas: buildCtas() } ),
		[ notifyDraft, notifyExtras, buildCtas ]
	);

	// Read at CLICK time, from the current draft — previewing a saved rule
	// would defeat the purpose.
	const buildPreviewRequest = useCallback( () => ( {
		kind: 'expected-item',
		check: buildCheck(),
		action: buildAction(),
	} ), [ buildCheck, buildAction ] );

	// An element a condition could mean several of (400 `ambiguous_element`,
	// element ids step 3): the person picks one, or any of these cards, and
	// the rule is saved again with the pick (`elementChoices`).
	// …answering exactly the check it was made for: any change forgets it.
	const elementPicks = useElementChoices( JSON.stringify( [ cardId, kql ] ) );
	const resetElementPicks = elementPicks.reset;
	// Each opening starts without picks.
	useEffect( () => { if ( open ) resetElementPicks(); }, [ open, resetElementPicks ] );

	const handleSave = async () => {
		// A weekly reminder with every box cleared would silently rebind to
		// whatever weekday the save happened on (the API treats an absent
		// daysOfWeek as "the anchor's weekday") — refuse instead.
		if ( unit === 'week' && ! daysOfWeek.length ) {
			message.error( 'Pick at least one weekday for a weekly reminder.' );
			return;
		}

		// The derived minutes are CLAMPED, so an over-long window would otherwise
		// save silently as 90 days — a reminder quietly narrower than the one the
		// manager wrote. Say so instead of correcting behind their back.
		if ( windowType === 'rolling' ) {
			if ( requestedWindowMinutes < 1 ) {
				message.error( 'Give the "time ago" window a length of at least one minute.' );
				return;
			}
			if ( windowTooLong ) {
				message.error( 'The "time ago" window can look back at most 90 days.' );
				return;
			}
		}

		const payload = {
			name: name.trim(),
			kind: 'expected-item',
			trigger: { type: 'schedule', schedule: buildSchedule() },
			check: buildCheck(),
			actions: [ buildAction() ],
		};

		setSaving( true );
		try {
			// merge:false — the payload above is the complete reminder, so the
			// stored check is REPLACED. A cleared `renotifyAfterMinutes` has no
			// null to send (the API takes an integer or nothing), so wholesale
			// replacement is what actually clears it. Ignored by an API that
			// predates the flag.
			if ( rule?._id ) await patchAutomation( collectionId, normalizeId( rule._id ), elementPicks.withChoices( { ...payload, merge: false } ) );
			else await createAutomation( collectionId, elementPicks.withChoices( payload ) );
			elementPicks.reset();
			message.success( rule?._id ? 'Reminder saved.' : 'Reminder created.' );
			onSaved?.();
		}
		catch ( err ) {
			// Verbatim: the API's 400s are field-level and written for humans.
			if ( elementPicks.take( err ) ) return;
			message.error( err?.response?.data?.message || 'Could not save the reminder.' );
		}
		finally {
			setSaving( false );
		}
	};

	/**
	 * A prefill value input shaped by the element it fills. A choice element
	 * gets its own choices, a date gets a picker, a lookup gets that card's
	 * items — anything else gets a token-aware text input, because "Missed at
	 * {{now}}" is a perfectly good prefill.
	 *
	 * Choices are asked about FIRST, by the shared predicate: a Choice element
	 * declaring `options.choices`, and nothing else. A short text, number or
	 * date carrying a list kept from an earlier life as a Choice gets its own
	 * input (KPE-033). See ChoiceOptions.js.
	 */
	const renderPrefillValue = row => {
		const element = ctaElementByName.get( row?.name );
		const dataType = element?.dataType;
		const ariaLabel = `Prefill value for ${ row?.name || 'element' }`;
		const style = { width: '100%' };

		if ( rendersAsChoice( element ) ) {
			return (
				<Select
					style={style}
					allowClear
					showSearch
					filterOption={optionSearchFilter}
					aria-label={ariaLabel}
					placeholder='Value'
					value={row.value === '' || row.value === null || row.value === undefined ? undefined : row.value}
					options={buildChoiceOptions( element?.options?.choices )}
					onChange={value => patchCtaRow( row.key, { value: value ?? '' } )}
				/>
			);
		}

		if ( dataType === 'date' || dataType === 'date-time' ) {
			const withTime = dataType === 'date-time';
			const parsed = row.value ? dayjs( row.value ) : null;
			return (
				<DatePicker
					style={style}
					aria-label={ariaLabel}
					showTime={withTime ? { format: 'HH:mm' } : false}
					value={parsed && parsed.isValid() ? parsed : null}
					// The stored shape the element type uses — 'YYYY-MM-DD' for a
					// date, an ISO instant for a date-time (utils/dateDisplay) —
					// because the prefill lands straight in a new-item form as
					// an element value.
					onChange={next => patchCtaRow( row.key, {
						value: next ? ( withTime ? toStoredDateTime( next ) : toStoredDate( next ) ) || '' : '',
					} )}
				/>
			);
		}

		if ( NUMERIC_DATA_TYPES.has( dataType ) ) {
			return (
				<InputNumber
					style={style}
					aria-label={ariaLabel}
					placeholder='Value'
					inputMode={getNumericInputMode( dataType )}
					value={row.value === '' || row.value === null ? null : row.value}
					onChange={value => patchCtaRow( row.key, { value: value === null || value === undefined ? '' : value } )}
				/>
			);
		}

		if ( dataType === 'measurement' ) {
			// The prefill lands in a new-item form as the element's value, so it
			// is emitted as the `{ value, unit }` the form (and the API) speak.
			return (
				<MeasurementValueInput
					// Keyed by element: a row re-pointed at another measurement
					// element opens a fresh control (leaving any text-mode
					// fallback, and the old element's unit, behind).
					key={`${ row.key }:${ row.name }`}
					id={`prefill-value-${ row.key }`}
					raw={row.value}
					options={element?.options}
					name={element?.name}
					label={elementLabelOf( element )}
					aria-label={ariaLabel}
					onChange={value => patchCtaRow( row.key, { value: value ?? '' } )}
				/>
			);
		}

		if ( dataType === 'currency' ) {
			// Emitted as the `{ amount, currency }` the form (and the API) speak.
			return (
				<CurrencyValueInput
					key={`${ row.key }:${ row.name }`}
					id={`prefill-value-${ row.key }`}
					raw={row.value}
					options={element?.options}
					name={element?.name}
					label={elementLabelOf( element )}
					aria-label={ariaLabel}
					onChange={value => patchCtaRow( row.key, { value: value ?? '' } )}
				/>
			);
		}

		if ( dataType === 'boolean' ) {
			// The prefill lands in a new-item form as the element's value, so
			// it commits a real boolean — the two states in the element's own
			// words, and nothing else to pick.
			const { trueLabel, falseLabel } = booleanLabels( element );
			return (
				<Select
					style={style}
					allowClear
					aria-label={ariaLabel}
					placeholder='Value'
					value={typeof row.value === 'boolean' ? row.value : undefined}
					options={[ { value: true, label: trueLabel }, { value: false, label: falseLabel } ]}
					onChange={value => patchCtaRow( row.key, { value: value === undefined ? '' : value } )}
				/>
			);
		}

		if ( dataType === 'card-lookup' ) {
			const targetId = normalizeId( element?.options?.lookupCardId );
			const entry = lookupItems[ targetId ];
			return (
				<Select
					style={style}
					allowClear
					showSearch
					filterOption={optionSearchFilter}
					aria-label={ariaLabel}
					loading={!! entry?.loading}
					placeholder={entry?.loading ? 'Loading items…' : 'Pick an item'}
					value={row.value || undefined}
					options={lookupOptionsFor( targetId )}
					onChange={value => patchCtaRow( row.key, { value: value || '' } )}
				/>
			);
		}

		return (
			<TokenInput
				value={typeof row.value === 'string' ? row.value : `${ row.value ?? '' }`}
				tokens={tokens}
				placeholder='Value'
				ariaLabel={ariaLabel}
				onChange={value => patchCtaRow( row.key, { value } )}
			/>
		);
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={rule?._id ? 'Edit reminder' : 'New reminder'}
			width={760}
			destroyOnClose
			footer={[
				<Button key='cancel' onClick={onClose}>Cancel</Button>,
				// Beside Save on purpose: "what will this actually say" is the
				// last question a manager asks before committing.
				<NotifyPreviewButton
					key='preview'
					collectionId={collectionId}
					buildRequest={buildPreviewRequest}
					disabled={! cardId}
				/>,
				<Button key='save' type='primary' loading={saving} onClick={handleSave}>Save</Button>,
			]}
		>
			<Space direction='vertical' size={12} style={{ width: '100%' }}>

				<div>
					<label htmlFor='expectedItemName' className='d-block'>Name</label>
					<Input
						id='expectedItemName'
						size='large'
						value={name}
						placeholder='What should be happening, in a few words'
						onChange={e => setName( e.target.value )}
					/>
				</div>

				<InputSelect
					elementName='expectedItemCard'
					elementLabel='Expected card'
					value={cardId || undefined}
					onChange={( n, value ) => setCardId( value || '' )}
					options={cardOptions}
					placeholder='Which card should have items?'
					allowClear={false}
				/>

				{cardId ? (<>
					<div>
						<Text strong className='d-block'>How often to check</Text>
					</div>

					<ScheduleFields
						value={scheduleValue}
						onChange={handleScheduleChange}
						idPrefix='expectedItem'
						// Several checks a day are one rule: the 8am / noon / evening
						// pass is one expectation with three moments, not three
						// reminders to keep in step by hand.
						allowMultipleTimes
						timeLabel='At'
						timezoneHint='Every window is measured in this zone.'
					/>


					<div>
						<Text strong className='d-block' id='expectedItemConditionLabel'>Only count items matching</Text>
						<ConditionBuilder
							value={kql}
							onChange={setKql}
							elements={expectedElements}
							collectionId={collectionId}
						/>
						<Text type='secondary'>
							Validated on save. No &#123;&#123; &#125;&#125; tokens — a scheduled check has no trigger item.
						</Text>
					</div>

					<div>
						<Text strong className='d-block'>Look at items since</Text>
						<Radio.Group
							value={windowType}
							onChange={e => setWindowType( e.target.value )}
							options={[
								{ label: 'Midnight', value: 'sinceMidnight' },
								{ label: 'The previous check', value: 'sinceSlot' },
								{ label: 'Time ago', value: 'rolling' },
							]}
						/>
					</div>

					{windowType === 'rolling' ? (
						<div>
							<Space align='start'>
								<div>
									<label htmlFor='expectedItemWindowQty' className='d-block'>How long ago</label>
									<InputNumber
										id='expectedItemWindowQty'
										inputMode='numeric'
										min={1}
										max={maxWindowQty}
										status={windowTooLong ? 'error' : ''}
										value={windowQty}
										onChange={value => setWindowQty( value )}
									/>
								</div>
								<div>
									<label htmlFor='expectedItemWindowUnit' className='d-block'>Unit</label>
									<Select
										id='expectedItemWindowUnit'
										style={{ width: 140 }}
										value={windowUnit}
										options={WINDOW_UNIT_OPTIONS}
										onChange={value => setWindowUnit( value )}
									/>
								</div>
							</Space>
							<Text type={windowTooLong ? 'danger' : 'secondary'} className='d-block'>
								{windowTooLong
									? `That is ${ requestedWindowMinutes.toLocaleString() } minutes — at most 90 days `
										+ `(${ WINDOW_MINUTES_MAX.toLocaleString() } minutes) can be looked back at.`
									: `Up to 90 days. Stored as ${ requestedWindowMinutes.toLocaleString() } minutes.`}
							</Text>
						</div>
					) : null}

					<div>
						<label htmlFor='expectedItemMinCount' className='d-block'>How many are expected</label>
						<InputNumber
							id='expectedItemMinCount'
							inputMode='numeric'
							min={1}
							value={minCount}
							onChange={value => setMinCount( value )}
						/>
					</div>

					<div>
						<label htmlFor='expectedItemRenotify' className='d-block'>Remind again after (minutes)</label>
						<InputNumber
							id='expectedItemRenotify'
							inputMode='numeric'
							min={5}
							value={renotifyAfterMinutes}
							placeholder='Never'
							onChange={value => setRenotifyAfterMinutes( value )}
						/>
						<Text type='secondary'>Leave empty to notify once per gap.</Text>
					</div>

					<Card size='small' title='Notify'>
						<NotifyActionFields
							draft={notifyDraft}
							onChange={setNotifyDraft}
							tokens={tokens}
							groups={groups}
							users={users}
						/>
					</Card>

					<Card
						size='small'
						title='Add a button to the notification'
						extra={(
							<Checkbox
								checked={ctaEnabled}
								onChange={e => setCtaEnabled( e.target.checked )}
								aria-label='Add a button to the notification'
							>
								Include
							</Checkbox>
						)}
					>
						{ctaEnabled ? (<>
							<div>
								<label htmlFor='expectedItemCtaLabel' className='d-block'>Button label</label>
								<Input
									id='expectedItemCtaLabel'
									value={ctaLabel}
									placeholder='Log it'
									onChange={e => setCtaLabel( e.target.value )}
								/>
							</div>

							<div style={{ marginTop: 12 }}>
								<InputSelect
									elementName='expectedItemCtaCard'
									elementLabel='Card the button creates'
									value={ctaCardId || undefined}
									onChange={( n, value ) => setCtaCardId( value || '' )}
									options={cardOptions}
									placeholder='Which card does the button start?'
									allowClear={false}
								/>
							</div>

							<div style={{ marginTop: 12 }}>
								<Text strong className='d-block'>Prefilled values</Text>
								{ctaRows.map( row => (
									<Space key={row.key} align='start' style={{ display: 'flex', marginBottom: 8 }}>
										<Select
											style={{ width: 200 }}
											value={row.name || undefined}
											placeholder='Element'
											aria-label='Prefill element'
											showSearch
											filterOption={optionSearchFilter}
											options={ctaElementOptions}
											// The value control changes shape with the element, so a
											// half-typed value from the previous type is cleared —
											// carrying "suzy" into a number field would emit a
											// prefill that the API rejects at save.
											onChange={value => patchCtaRow( row.key, { name: value, value: '' } )}
										/>
										<div style={{ flex: '1 1 auto', minWidth: 220 }}>
											{renderPrefillValue( row )}
										</div>
										<Button
											type='text'
											danger
											icon={<CLOSE />}
											aria-label='Remove this prefilled value'
											onClick={() => setCtaRows( prev => prev.filter( r => r.key !== row.key ) )}
										/>
									</Space>
								) )}
								<Button
									size='small'
									icon={<PLUS />}
									onClick={() => setCtaRows( prev => [ ...prev, emptyRow() ] )}
								>
									Add a prefilled value
								</Button>
							</div>
						</>) : (
							<Text type='secondary'>
								A button takes the reader straight to a new item, with values already filled in.
							</Text>
						)}
					</Card>

				</>) : null}


				<QueryRefusalPick
					inline
					refusal={elementPicks.refusal}
					disabled={saving}
					onPick={value => { elementPicks.pick( value ); handleSave(); }}
					onRepick={() => { elementPicks.clearRefusal(); handleSave(); }}
				/>
			</Space>
		</Modal>
	);
}
